<template>
  <form-layout :label="label" :required="required" :optional="optional">
    <div class="position-relative">
      <input type="text"
        class="form-control"
        :class="errorMessage ? 'is-invalid' : ''"
        autocomplete="off"
        :placeholder="placeholder"
        v-model="searchBuildingName"
        @input="onInput"
        @blur="onBlur"
        @focus="onFocus">
      <div v-if="shouldShowDropdown"
        class="position-absolute bg-white border rounded mt-1"
        style="width: 100%; z-index: 1000; max-height: 300px; overflow-y: auto;">
        <div v-for="(buildingSearchResult, index) in filteredResults"
          :key="buildingSearchResult.id"
          class="px-3 py-2"
          :class="selectedIndex === index ? 'bg-light' : ''"
          style="cursor: pointer;"
          @click="selectBuilding(buildingSearchResult)"
          @mouseover="selectedIndex = index">
          {{ buildingSearchResult.name }}
        </div>
      </div>
      <div class="invalid-feedback">{{ errorMessage }}</div>
    </div>
    <span v-if="warningMessage" class="form-text text-secondary">{{ warningMessage }}</span>
  </form-layout>
</template>
<script>
import BaseVuexFormItem from "@/views/molecules/formItems/BaseVuexFormItem";
import FormLayout from "@/views/templates/FormLayout";

export default {
  name: "VuexBuildingTextFormItem",
  extends: BaseVuexFormItem,
  components: {
    FormLayout,
  },
  props: {
    label: {
      type: String,
      default: '依頼物件選択'
    },
    required: {
      type: Boolean,
      default: false
    },
    optional: {
      type: Boolean,
      default: false
    },
    options: {
      type: Array,
      required: true
    },
    minlength: {
      type: [Number, null],
      default: null
    },
    placeholder: {
      type: String,
      required: true
    },
    searchType: {
      type: Number,
      required: true
    },
  },
  emits: ['select'],
  data() {
    return {
      searchBuildingName: null,
      isFocused: false,
      selectedIndex: -1,
    }
  },
  computed : {
    warningMessage() {
      if (!!this.searchBuildingName && this.value === null) {
        return "入力された依頼物件が存在しません。";
      }
      return null;
    },
    filteredResults() {
      const keyword = this.searchBuildingName;
      if (!keyword) return [];

      const minLen = this.minlength || 0;
      const normalizedSearch = this.normalizeKana(keyword);

      return this.options.filter((building) => {
        const normalizedName = this.normalizeKana(building.name);
        const normalizedNameKana = building.name_kana
          ? this.normalizeKana(building.name_kana)
          : "";

        if (keyword.length < minLen) {
          return (
            normalizedName === normalizedSearch ||
            normalizedNameKana === normalizedSearch
          );
        }

        if (this.searchType === 1) {
          return (
            normalizedName.includes(normalizedSearch) ||
            normalizedNameKana.includes(normalizedSearch)
          );
        }

        if (this.searchType === 2) {
          return (
            normalizedName.startsWith(normalizedSearch) ||
            normalizedNameKana.startsWith(normalizedSearch)
          );
        }

        return (
          normalizedName === normalizedSearch ||
          normalizedNameKana === normalizedSearch
        );
      });
    },
    shouldShowDropdown() {
      return this.isFocused && this.filteredResults.length > 0;
    },
  },
  mounted() {
    if (this.value) {
      const filtered = this.options.filter((building) => building.id === this.value);
      this.searchBuildingName = filtered.length > 0 ? filtered[0].name : null;
    }
  },
  methods: {
    normalizeKana(str) {
      // 半角カタカナを全角カタカナに変換（濁音・半濁音対応、小文字・古文字含む）
      const hankakuMap = {
        // 基本文字
        'ｱ': 'ア', 'ｲ': 'イ', 'ｳ': 'ウ', 'ｴ': 'エ', 'ｵ': 'オ',
        'ｶ': 'カ', 'ｷ': 'キ', 'ｸ': 'ク', 'ｹ': 'ケ', 'ｺ': 'コ',
        'ｻ': 'サ', 'ｼ': 'シ', 'ｽ': 'ス', 'ｾ': 'セ', 'ｿ': 'ソ',
        'ﾀ': 'タ', 'ﾁ': 'チ', 'ﾂ': 'ツ', 'ﾃ': 'テ', 'ﾄ': 'ト',
        'ﾅ': 'ナ', 'ﾆ': 'ニ', 'ﾇ': 'ヌ', 'ﾈ': 'ネ', 'ﾉ': 'ノ',
        'ﾊ': 'ハ', 'ﾋ': 'ヒ', 'ﾌ': 'フ', 'ﾍ': 'ヘ', 'ﾎ': 'ホ',
        'ﾏ': 'マ', 'ﾐ': 'ミ', 'ﾑ': 'ム', 'ﾒ': 'メ', 'ﾓ': 'モ',
        'ﾔ': 'ヤ', 'ﾕ': 'ユ', 'ﾖ': 'ヨ',
        'ﾗ': 'ラ', 'ﾘ': 'リ', 'ﾙ': 'ル', 'ﾚ': 'レ', 'ﾛ': 'ロ',
        'ﾜ': 'ワ', 'ｦ': 'ヲ', 'ﾝ': 'ン',
        // 小文字
        'ｨ': 'ィ', 'ｪ': 'ェ', 'ｩ': 'ゥ', 'ｫ': 'ォ', 'ｯ': 'ッ', 'ｮ': 'ョ', 'ｭ': 'ュ',
        // 濁音
        'ｶﾞ': 'ガ', 'ｷﾞ': 'ギ', 'ｸﾞ': 'グ', 'ｹﾞ': 'ゲ', 'ｺﾞ': 'ゴ',
        'ｻﾞ': 'ザ', 'ｼﾞ': 'ジ', 'ｽﾞ': 'ズ', 'ｾﾞ': 'ゼ', 'ｿﾞ': 'ゾ',
        'ﾀﾞ': 'ダ', 'ﾁﾞ': 'ヂ', 'ﾂﾞ': 'ヅ', 'ﾃﾞ': 'デ', 'ﾄﾞ': 'ド',
        'ﾊﾞ': 'バ', 'ﾋﾞ': 'ビ', 'ﾌﾞ': 'ブ', 'ﾍﾞ': 'ベ', 'ﾎﾞ': 'ボ',
        // 半濁音
        'ﾊﾟ': 'パ', 'ﾋﾟ': 'ピ', 'ﾌﾟ': 'プ', 'ﾍﾟ': 'ペ', 'ﾎﾟ': 'ポ',
        // 長音
        'ｰ': 'ー'
      };

      let result = str;
      // 濁音・半濁音を先に処理（複数文字）
      for (const [key, value] of Object.entries(hankakuMap)) {
        if (key.length > 1) {
          result = result.replace(new RegExp(key, 'g'), value);
        }
      }
      // 単一文字を処理
      for (const [key, value] of Object.entries(hankakuMap)) {
        if (key.length === 1) {
          result = result.replace(new RegExp(key, 'g'), value);
        }
      }

      // カタカナをひらがなに変換
      result = result.replace(/[\u30A1-\u30F6]/g, function(c) {
        return String.fromCharCode(c.charCodeAt(0) - 0x60);
      });
      return result;
    },
    onInput() {
      this.selectedIndex = -1;
      const normalizedSearch = this.normalizeKana(this.searchBuildingName ?? '');
      const exactMatchedBuilding = this.options.find((building) => {
        const normalizedName = this.normalizeKana(building.name ?? '');
        const normalizedNameKana = building.name_kana
          ? this.normalizeKana(building.name_kana)
          : '';
        return (
          normalizedName === normalizedSearch ||
          normalizedNameKana === normalizedSearch
        );
      });
      if (exactMatchedBuilding) {
        this.value = exactMatchedBuilding.id;
        this.$emit('select', this.value);
        return;
      }
      this.value = null;
      this.$emit('select', this.value);
    },
    onBlur() {
      setTimeout(() => {
        this.isFocused = false;
      }, 200);
    },
    onFocus() {
      this.isFocused = true;
    },
    selectBuilding(building) {
      this.searchBuildingName = building.name;
      this.value = building.id;
      this.isFocused = false;
      this.$emit('select', this.value);
    },
  },
};
</script>